import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Column, Spacer } from '../../../components';
import { PricebookBundle } from '../../../models/PricebookData';
import { RestrictionMode } from '../helpers/pricebookRestrictionUtils';
import PricebookRestrictionSection from './PricebookRestrictionSection';

interface PricebookRestrictionSelectorProps {
  heading: string;
  parentTierLabel: string;
  tierLabel: string;
  mode: RestrictionMode;
  onModeChange: (mode: RestrictionMode) => void;
  memberPaysPricebooks: PricebookBundle[];
  orgPaysPricebooks: PricebookBundle[];
  addOnPricebookLookup: Map<number, PricebookBundle>;
  inUsePricebookIds: Set<number>;
  selectedPricebookIds: Set<number>;
  onTogglePricebook: (pricebookId: number) => void;
}

const PricebookRestrictionSelector: React.FC<PricebookRestrictionSelectorProps> = ({
  heading,
  parentTierLabel,
  tierLabel,
  mode,
  onModeChange,
  memberPaysPricebooks,
  orgPaysPricebooks,
  addOnPricebookLookup,
  inUsePricebookIds,
  selectedPricebookIds,
  onTogglePricebook,
}) => {
  const isInheritMode = mode === 'inherit';

  return (
    <Column>
      <Typography variant='p18SemiBold' color='secondary.dark'>
        {heading}
      </Typography>

      <Spacer height='xxs' />

      <ToggleButtonGroup
        value={mode}
        exclusive
        onChange={(_, newValue) => {
          if (newValue === 'inherit' || newValue === 'override' || newValue === 'advanced')
            onModeChange(newValue);
        }}
        fullWidth>
        <ToggleButton value='inherit'>Inherit</ToggleButton>
        <ToggleButton value='override'>Override</ToggleButton>
        <ToggleButton value='advanced'>Advanced</ToggleButton>
      </ToggleButtonGroup>

      <Spacer height='sm' />

      <Typography variant='p18SemiBold' color='secondary.dark'>
        {isInheritMode
          ? `The following pricebooks are inherited from your ${parentTierLabel ?? tierLabel}.`
          : 'Select from the following pricebooks'}
      </Typography>

      <Spacer height='xxxs' />

      {!isInheritMode && (
        <Typography variant='p16' color='info.main'>
          Select which ones you want to make available to this {tierLabel}.
        </Typography>
      )}

      <Spacer height='sm' />

      <Box display='flex' flexDirection='column' rowGap={5}>
        <PricebookRestrictionSection
          title='Member pays'
          accentColor='primary.main'
          pricebooks={memberPaysPricebooks}
          addOnPricebookLookup={addOnPricebookLookup}
          inUsePricebookIds={inUsePricebookIds}
          isInheritMode={isInheritMode}
          selectedPricebookIds={selectedPricebookIds}
          onTogglePricebook={onTogglePricebook}
        />
        <PricebookRestrictionSection
          title='Organization pays'
          accentColor='alternate.main'
          pricebooks={orgPaysPricebooks}
          addOnPricebookLookup={addOnPricebookLookup}
          inUsePricebookIds={inUsePricebookIds}
          isInheritMode={isInheritMode}
          selectedPricebookIds={selectedPricebookIds}
          onTogglePricebook={onTogglePricebook}
        />
      </Box>
    </Column>
  );
};

export default PricebookRestrictionSelector;
